// © Thibault Sabathier, 2026. Tous droits réservés.
// frontend/src/utils/memberSearchResponses.js
//
// « Je peux aider » (lot 3) : LOGIQUE PURE de l'interface des propositions (aucun accès réseau ni DOM ici, tout est testé
// sans navigateur). Les écrans (ResponsesModal, MyResponses, RespondModal) ne font que l'afficher.
//
// Principes :
//   - le serveur reste l'autorité : les actions proposées (`canAccept`, `canDecline`, `canMarkDone`, `canWithdraw`) sont celles
//     qu'il renvoie, l'interface ne recalcule aucun droit ;
//   - le REFUS est silencieux : l'aidant le voit comme « sans suite » (`closed`), jamais comme un refus ; l'interface n'invente
//     donc aucun libellé « refusé » côté aidant ;
//   - un membre effacé (RGPD) arrive avec `id: null` : l'interface affiche SON libellé traduit, jamais « Membre supprimé ».

import { parseApiError, fieldErrorKeyFor } from './memberSearches'

/** Plafond du texte d'une proposition (miroir de config/memberSearches.js du backend, qui reste l'autorité). */
export const RESPONSE_MESSAGE_MAX = 500

export const AUTHOR_VIEW_STATUSES = [
  'pending',
  'accepted',
  'declined',
  'withdrawn',
  'expired',
  'done',
]
export const HELPER_VIEW_STATUSES = [
  'pending',
  'accepted',
  'done',
  'closed',
  'withdrawn',
  'expired',
]

// ─── Erreurs ───────────────────────────────────────────────────────────────

/** Code d'erreur API → clé du namespace `searches`. Tout code inconnu retombe sur `errors.generic`. */
const RESPONSE_ERROR_KEYS = {
  RESPONSE_NOT_FOUND: 'responses.errors.not_found',
  SEARCH_NOT_FOUND: 'responses.errors.search_gone',
  INVALID_TRANSITION: 'responses.errors.already_handled',
  PARTY_UNAVAILABLE: 'responses.errors.party_unavailable',
  SEARCH_CLOSED: 'responses.errors.search_closed',
  SEARCH_EXPIRED: 'responses.errors.search_expired',
  SEARCH_RESPONSE_LIMIT_REACHED: 'responses.errors.search_limit',
  HELPER_LIMIT_REACHED: 'responses.errors.helper_limit',
  SEARCH_NOT_RESPONDABLE: 'responses.errors.not_respondable',
  CANNOT_RESPOND_OWN_SEARCH: 'responses.errors.own_search',
  RESPONSE_NOT_REPROPOSABLE: 'responses.errors.not_reproposable',
  RESPONSE_ALREADY_EXISTS: 'responses.errors.already_exists',
  NOT_ELIGIBLE: 'responses.errors.not_eligible',
  CONCURRENT_UPDATE: 'responses.errors.busy',
  COMMIT_UNCERTAIN: 'responses.errors.uncertain',
  ROLLBACK_UNCONFIRMED: 'responses.errors.uncertain',
  VALIDATION: 'responses.errors.validation',
  TOO_MANY_REQUESTS: 'errors.rate_limited',
  MEMBER_NOT_VALIDATED: 'errors.not_validated',
  MODULE_DISABLED: 'errors.module_disabled',
}

export const responseErrorKeyFor = (code) => RESPONSE_ERROR_KEYS[code] || 'errors.generic'

/**
 * Codes qui signifient « l'état affiché est périmé » (déjà traitée, annonce close ou expirée, conflit concurrent, partie
 * indisponible) : l'écran recharge la liste après avoir expliqué l'échec, au lieu de laisser des boutons inopérants.
 */
export const STALE_STATE_CODES = new Set([
  'RESPONSE_NOT_FOUND',
  'INVALID_TRANSITION',
  'PARTY_UNAVAILABLE',
  'SEARCH_CLOSED',
  'SEARCH_EXPIRED',
  'SEARCH_NOT_FOUND',
  'CONCURRENT_UPDATE',
  'RESPONSE_ALREADY_EXISTS',
])

// ─── Présentation ──────────────────────────────────────────────────────────

export const responseStatusColor = (status) =>
  ({ pending: 'primary', accepted: 'success', done: 'success' })[status] || 'secondary'

/** Identifiant de conversation exploitable (entier > 0) ou null. */
export const usableConversationId = (value) => {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean')
    return null
  const n = Number(value)
  return Number.isInteger(n) && n > 0 ? n : null
}

/** Conversation à ouvrir pour une proposition : seulement `accepted` / `done` (le serveur n'en pose jamais ailleurs). */
export const conversationIdOf = (response) =>
  response && (response.status === 'accepted' || response.status === 'done')
    ? usableConversationId(response.conversationId)
    : null

/** Aidant effacé (RGPD) ou compte système : `id` nul. L'appelant affiche sa propre traduction. */
export const isDeletedMember = (member) => Boolean(member) && member.id == null

/** { deleted, name, subtitle, profileId } d'un aidant (ou d'un auteur d'annonce) pour l'affichage. */
export const describeMember = (member) => {
  if (!member || isDeletedMember(member))
    return { deleted: true, name: '', subtitle: '', profileId: null }
  return {
    deleted: false,
    name: [member.firstName, member.lastName].filter(Boolean).join(' '),
    subtitle: [member.position, member.company].filter(Boolean).join(' · '),
    profileId: usableConversationId(member.id),
  }
}

/** Propositions en attente d'abord, puis la plus récente d'abord (ordre stable, ne modifie pas l'entrée). */
export const orderForAuthor = (responses) =>
  (Array.isArray(responses) ? [...responses] : []).sort((a, b) => {
    const pa = a.status === 'pending' ? 0 : 1
    const pb = b.status === 'pending' ? 0 : 1
    if (pa !== pb) return pa - pb
    return String(b.createdAt || '').localeCompare(String(a.createdAt || ''))
  })

// ─── Côté aidant : que proposer sur une annonce du réseau ? ────────────────

/**
 * État d'une annonce du réseau pour l'AIDANT, d'après ma proposition éventuelle (`response`, vue aidant).
 *   none        : rien à afficher (ma propre annonce, autre type d'annonce, annonce non active sans proposition)
 *   can_help    : bouton « Je peux aider » (première proposition, ou reproposition après expiration)
 *   pending / accepted / done / closed / withdrawn / expired : statut de ma proposition (closed = « sans suite », jamais « refusée »)
 * Une reproposition n'est possible QUE après `expired` et si l'annonce est de nouveau active (règle du serveur).
 */
export const helperStateFor = (search, response) => {
  if (!search || search.kind !== 'intro_request' || search.isMine) return { kind: 'none' }
  if (!response)
    return search.status === 'active' ? { kind: 'can_help', reproposal: false } : { kind: 'none' }
  if (response.status === 'expired') {
    return search.status === 'active' ? { kind: 'can_help', reproposal: true } : { kind: 'expired' }
  }
  const state = { kind: HELPER_VIEW_STATUSES.includes(response.status) ? response.status : 'none' }
  const conversationId = conversationIdOf(response)
  return conversationId ? { ...state, conversationId } : state
}

/** Index « annonce → ma proposition » (vue aidant). */
export const indexBySearch = (responses) =>
  new Map(
    (Array.isArray(responses) ? responses : [])
      .filter((r) => r && r.searchId != null)
      .map((r) => [Number(r.searchId), r]),
  )

// ─── Texte d'une proposition ───────────────────────────────────────────────

/**
 * Contrôle local AVANT l'envoi (le serveur revalide : texte brut, 1 à 500 caractères, HTML refusé).
 * @returns {{ok: true, value: string} | {ok: false, code: 'REQUIRED'|'TOO_LONG'}}
 */
export const validateHelpMessage = (text) => {
  const value = typeof text === 'string' ? text.trim() : ''
  if (!value) return { ok: false, code: 'REQUIRED' }
  if (value.length > RESPONSE_MESSAGE_MAX) return { ok: false, code: 'TOO_LONG' }
  return { ok: true, value }
}

/** Clé d'erreur du champ « message » (réponse 422 du serveur ou contrôle local), ou null. */
export const messageFieldErrorKey = (codeOrError) => {
  const code =
    typeof codeOrError === 'string' ? codeOrError : parseApiError(codeOrError).fields.message
  return code ? fieldErrorKeyFor(code) : null
}

// ─── Exécution d'une action : verrou anti double envoi, erreur traduite, rechargement ──────────

/**
 * Exécute UNE action sur une proposition (accepter, refuser, confirmer, retirer, proposer).
 *   - `lock` : { current: boolean }, partagé par l'écran : une action en cours bloque les suivantes (double clic, deux
 *     boutons) SANS dépendre du rendu ;
 *   - succès : { ok: true, result, notice: {color: 'success', key: successKey}, reload: true } ;
 *   - échec  : { ok: false, code, fields, notice: {color: 'danger', key}, reload } où `reload` vaut true quand l'état affiché est
 *     périmé (STALE_STATE_CODES) ;
 *   - action ignorée (verrou pris) : { skipped: true }.
 * Ne lève jamais : toute erreur du client API est lue par parseApiError.
 */
export async function runResponseAction({ lock, call, successKey }) {
  if (lock.current) return { skipped: true }
  lock.current = true
  try {
    const result = await call()
    return { ok: true, result, notice: { color: 'success', key: successKey }, reload: true }
  } catch (error) {
    const { code, fields } = parseApiError(error)
    return {
      ok: false,
      code,
      fields,
      notice: { color: 'danger', key: responseErrorKeyFor(code) },
      reload: STALE_STATE_CODES.has(code),
    }
  } finally {
    lock.current = false
  }
}

// ─── Compteurs de propositions en attente sur mes annonces ─────────────────

/** Mes annonces dont le compteur est utile : une demande de mise en relation ACTIVE (seule porte des propositions). */
export const searchesWithLiveProposals = (searches) =>
  (Array.isArray(searches) ? searches : []).filter(
    (s) => s && s.kind === 'intro_request' && s.status === 'active' && s.isMine !== false,
  )

/**
 * Nombre de propositions EN ATTENTE par annonce (Map id → nombre). `fetchPending(id)` renvoie la réponse de l'API
 * (`pagination.total`). Un échec isolé n'affiche simplement aucun compteur pour cette annonce.
 */
export async function loadPendingCounts({ searches, fetchPending }) {
  const targets = searchesWithLiveProposals(searches)
  const settled = await Promise.allSettled(targets.map((s) => fetchPending(s.id)))
  const counts = new Map()
  settled.forEach((outcome, index) => {
    if (outcome.status !== 'fulfilled') return
    const body = outcome.value || {}
    const total =
      body.pagination && Number.isFinite(body.pagination.total)
        ? body.pagination.total
        : (body.responses || []).length
    counts.set(targets[index].id, total)
  })
  return counts
}

// ─── Navigation ────────────────────────────────────────────────────────────

/**
 * Ouvre la conversation flottante (même événement que la cloche et le dock). Ne fait rien, et renvoie false, pour un
 * identifiant inexploitable : jamais d'événement avec NaN.
 */
export const requestOpenConversation = (
  conversationId,
  target = typeof window !== 'undefined' ? window : null,
) => {
  const id = usableConversationId(conversationId)
  if (!id || !target || typeof target.dispatchEvent !== 'function') return false
  target.dispatchEvent(
    new CustomEvent('openFloatingConversation', { detail: { conversationId: id } }),
  )
  return true
}
